import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';

import type { W_Equipment_FindAllDeletedForCompanyTable_VM_Row } from 'ab-api/web/equipment/findAllDeletedForCompanyTable';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { BlobStorageImageSizeContainer } from '@acceligentllc/shared/enums/blobStorageImageSizeContainer';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { RootState } from 'af-reducers';

import { downloadCSV } from 'af-utils/csv.utils';

import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import CLIENT from 'af-constants/routes/client';

import { DEFAULT_EQUIPMENT_IMAGE } from 'ab-common/constants/value';

import { loadingToast, successToast } from '@acceligentllc/storybook';

import ImageTag from 'af-components/Image';
import LabelWithColor from 'af-components/LabelWithColor';
import StatusLabel from 'af-components/StatusLabel';
import Table from 'af-components/Table6';
import type { TabProps, Column, ButtonData } from 'af-components/Table6';
import EmptyCell from 'af-components/Table6/Cells/EmptyCell';
import LastUpdatedByCell from 'af-components/Table6/Cells/LastUpdatedByCell';
import SkillsCell from 'af-components/Table6/Cells/SkillsCell';

import * as EquipmentActions from 'af-actions/equipment';

import { toCSVData } from './helpers';

type OwnProps = CustomRouteComponentProps;

type Props = OwnProps & ConnectedProps<typeof connector>;

const IMAGE_COLUMN_WIDTH = 100;
const TYPE_COLUMN_WIDTH = 80;
const UPDATED_COLUMN_WIDTH = 200;

const DeletedEquipmentList = (props: Props) => {
	const {
		history,
		location: { state: { orgAlias } },
		companyName,
		findAllDeletedForCompanyList,
		findAllDeletedForCompanyTable,
	} = props;

	const [filterText, setFilterText] = React.useState('');

	const COLUMNS: Column<W_Equipment_FindAllDeletedForCompanyTable_VM_Row>[] = [
		{
			accessor: 'imageUrl',
			sortable: false,
			width: IMAGE_COLUMN_WIDTH,
			className: 'rt-thumbnail',
			Cell: ({ original }) => (
				<ImageTag
					fallbackSrc={DEFAULT_EQUIPMENT_IMAGE}
					minSize={BlobStorageImageSizeContainer.SIZE_50X50}
					src={original.imageUrl}
					tryOriginal={true}
					tryRoot={true}
				/>
			),
		},
		{
			Header: 'ID',
			accessor: 'code',
			Cell: ({ original }) => {
				const text = <>{original.code} {original.specification ? <span className="small-label">{original.specification}</span> : ''}</>;
				return <LabelWithColor color={original.equipmentCostCategoryColor} text={text} />;
			},
		},
		{
			Header: 'Type',
			minWidth: TYPE_COLUMN_WIDTH,
			accessor: 'equipmentCostGroupType',
			Cell: ({ original }) => original.equipmentCostGroupType?.[0],
		},
		{
			Header: 'Group',
			accessor: 'equipmentCostGroupName',
			Cell: ({ original }) => original.equipmentCostGroupName,
		},
		{
			Header: 'Category',
			accessor: 'equipmentCostCategoryName',
			Cell: ({ original }) => original.equipmentCostCategoryName,
		},
		{
			Header: 'Subcategory',
			accessor: 'equipmentCostSubcategory',
			Cell: ({ original }) => original.equipmentCostSubcategory,
		},
		{
			Header: 'Skills',
			accessor: 'allSkills',
			sortable: false,
			Cell: ({ original }) => <SkillsCell skills={original.allSkills} />,
		},
		{
			Header: 'Home Office',
			accessor: 'locationName',
			Cell: ({ original }) => original.locationName ?? <EmptyCell />,
		},
		{
			Header: 'Last Status',
			sortable: false,
			accessor: 'equipmentStatusAvailability',
			Cell: ({ original }) => <StatusLabel isAvailable={original.equipmentStatusAvailability} label={original.equipmentStatusName} />,
		},
		{
			Header: 'Updated',
			width: UPDATED_COLUMN_WIDTH,
			headerClassName: 'align-right padding-right-l',
			accessor: 'updatedAt',
			Cell: ({ original }) => <LastUpdatedByCell updatedAt={original.updatedAt} updatedBy={original.updatedBy} />,
		},
		{
			Header: 'Plate Number',
			accessor: 'plateNumber',
			Cell: ({ original }) => original.plateNumber ?? <EmptyCell />,
		},
		{
			Header: 'Plate Type',
			accessor: 'plateType',
			Cell: ({ original }) => original.plateType ?? <EmptyCell />,
		},
		{
			Header: 'Registration State',
			accessor: 'registrationState',
			Cell: ({ original }) => original.registrationState ?? <EmptyCell />,
		},
		{
			Header: 'Registration Exp.',
			accessor: 'registrationExpiration',
			Cell: ({ original }) => original.registrationExpiration
				? TimeUtils.formatDate(original.registrationExpiration, TimeFormat.DATE_ONLY_SLASHES, TimeFormat.DB_DATE_ONLY)
				: <EmptyCell />,
		},
		{
			Header: 'Inspection Exp.',
			accessor: 'inspectionStickerExpiration',
			Cell: ({ original }) => original.inspectionStickerExpiration ?
				TimeUtils.formatDate(original.inspectionStickerExpiration, TimeFormat.DATE_ONLY_SLASHES, TimeFormat.DB_DATE_ONLY)
				: <EmptyCell />,
		},
		{
			Header: 'VIN',
			accessor: 'vinNumber',
			Cell: ({ original }) => original.vinNumber ?? <EmptyCell />,
		},
		{
			Header: 'GVW',
			accessor: 'gvw',
			Cell: ({ original }) => (original.gvw !== null ? original.gvw.toString() : <EmptyCell />),
		},
		{
			Header: 'Year',
			accessor: 'year',
			Cell: ({ original }) => (original.year !== null ? original.year.toString() : <EmptyCell />),
		},
		{
			Header: 'Make',
			accessor: 'make',
			Cell: ({ original }) => original.make ?? <EmptyCell />,
		},
		{
			Header: 'Model',
			accessor: 'model',
			Cell: ({ original }) => original.model ?? <EmptyCell />,
		},
		{
			Header: 'Drivers Alert ID',
			accessor: 'driversAlertId',
			Cell: ({ original }) => original.driversAlertId ?? <EmptyCell />,
		},
		{
			Header: 'Toll Pass ID',
			accessor: 'tollPassId',
			Cell: ({ original }) => original.tollPassId ?? <EmptyCell />,
		},
		{
			Header: 'HUT Number',
			accessor: 'hutNumber',
			Cell: ({ original }) => original.hutNumber ?? <EmptyCell />,
		},

	];

	const previewEquipment = async (original: W_Equipment_FindAllDeletedForCompanyTable_VM_Row) => {
		history.push(CLIENT.COMPANY.EQUIPMENT.PREVIEW(original.id.toString(), orgAlias, companyName));
	};

	const onRowClick = ({ original }: { original: W_Equipment_FindAllDeletedForCompanyTable_VM_Row; }) => {
		if (original.id) {
			previewEquipment(original);
		}
	};

	const onDownloadCSVClick = async () => {
		const toastId = 'equipment-export';

		loadingToast(
			toastId,
			{ title: 'Exporting equipment...', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
		);

		const listViewModel = await findAllDeletedForCompanyList(filterText);

		const filename = `${companyName}_deleted_equipment.csv`;
		downloadCSV(toCSVData(listViewModel), filename);

		successToast(
			toastId,
			{ title: 'Equipment export complete', description: filename, timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
		);
	};

	const tabs = (): TabProps<W_Equipment_FindAllDeletedForCompanyTable_VM_Row>[] => {

		const buttons: ButtonData[] = [
			{
				type: TableButtonType.EXPORT,
				hasPermission: true,
				onClick: onDownloadCSVClick,
			},
		];

		return [
			{
				label: 'Deleted Equipment',
				columns: COLUMNS,
				hasSearchInput: true,
				searchLabel: 'Deleted Equipment',
				fetch: findAllDeletedForCompanyTable,
				buttons,
				onRowClick: onRowClick,
				rowActions: [
					{
						label: 'Preview',
						action: previewEquipment,
						shouldRefresh: false,
					},
				],
			},
		];
	};

	return (
		<Table
			offsetHeight={20}
			onFilterTextChange={setFilterText}
			tableName={TableNameEnum.DELETED_EQUIPMENT}
			tabs={tabs()}
		/>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		findAllDeletedForCompanyTable: EquipmentActions.findAllDeletedForCompanyTable,
		findAllDeletedForCompanyList: EquipmentActions.findAllDeletedForCompanyList,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass>(
	withRouter,
	connector
);

export default enhance(DeletedEquipmentList);
